import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton, cx } from './ui';

/**
 * The single overlay primitive — escape handling and scroll locking live here once.
 *
 * Dark scrim + a 2px blur, never heavy glass. Backdrop click closes: unlike Alchemist2's
 * data-entry modals, everything here is small and re-openable, so guarding against an
 * accidental dismiss costs more than it saves on a touch screen.
 */
export function Modal({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  eyebrow?: string;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const width = { sm: 440, md: 560, lg: 720 }[size];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:px-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onMouseDown={(e) => {
        // Only a press that starts AND ends on the backdrop dismisses, so a drag that
        // began inside the panel can't close it.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          'flex w-full flex-col overflow-hidden border border-border bg-surface-high',
          'rounded-t-lg sm:rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.45)]',
        )}
        style={{ maxWidth: width, maxHeight: '90vh' }}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border-subtle px-5 py-3">
          <div className="min-w-0">
            {eyebrow && <span className="eyebrow">{eyebrow}</span>}
            <h2 className="mt-0.5 truncate font-display text-[18px] leading-tight text-primary">{title}</h2>
          </div>
          <IconButton icon="close" label="Close" size="sm" onClick={onClose} className="-mt-1 -mr-1.5" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex shrink-0 items-center gap-2 border-t border-border-subtle bg-surface/60 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
